/**
 * Adapts PaginatedSelect JSON (AppLazySelect fetchers) to ZoneSelect's server `fetcher`.
 *
 * @param {Object} config
 * @param {(payload: Record<string, unknown>) => Promise<{
 *   items?: Array<{ id: number, name: string, avatar?: string, disabled?: boolean, removed?: boolean }>,
 *   pagination?: { last_page?: boolean, next_page?: number | null },
 *   disabled_ids?: number[],
 *   selected_disabled_ids?: number[],
 * }>} config.request
 * @param {(item: object) => Record<string, unknown>} [config.mapOption]
 * @param {number} [config.pageSize=50]
 * @param {boolean} [config.disableSelectedDisabledIds=true] When false, `selected_disabled_ids` are not
 *   marked disabled on options (project role pickers: allow toggling team-sourced members).
 */
export function createPaginatedSelectZoneFetcher({
  request,
  mapOption,
  pageSize = 50,
  disableSelectedDisabledIds = true,
}) {
  return async (state) => {
    const selectionType = state.selectionType === 'select_all' ? 'select_all' : 'normal';

    const response = await request({
      search: state.search || '',
      page: state.page,
      selected_only: state.selectedOnly,
      selection: state.selected,
      selection_type: selectionType,
    });

    const disabledIds = new Set([
      ...(response.disabled_ids || []),
      ...(disableSelectedDisabledIds ? (response.selected_disabled_ids || []) : []),
    ]);

    const options = (response.items || []).map((item) => {
      const base = {
        id: item.id,
        name: item.name,
        avatar: item.avatar,
        disabled: Boolean(item.disabled) || disabledIds.has(item.id),
      };

      if (mapOption) {
        return { ...base, ...mapOption(item) };
      }

      return base;
    });

    // Match AppLazySelect: Kaminari sets next_page to null on the last page (including empty results).
    const hasMore = response.pagination?.next_page != null;

    return {
      options,
      hasMore,
      selectedIds: response.selected_ids,
    };
  };
}
